import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  loadBrowserProductionDashboardApplication,
  type BrowserProductionDashboardApplication,
} from "./application/browser-production-loader";
import {
  DEFAULT_DASHBOARD_ANALYSIS_CONTROLS,
  type DashboardAnalysisControls,
  type DashboardAnalysisRequest,
  type DashboardCurrentKnowledgeReady,
  type DashboardCurrentKnowledgeResult,
  type DashboardPairCatalogEntry,
} from "./application/types";
import { AnalysisError } from "./components/AnalysisError";
import { AnalysisUnavailable } from "./components/AnalysisUnavailable";
import { DashboardHeader } from "./components/DashboardHeader";
import { DashboardTabs, type DashboardTabId } from "./components/DashboardTabs";
import { FilterBar } from "./components/FilterBar";
import { LazyPanelBoundary } from "./components/LazyPanelBoundary";
import type {
  ContractPairSelection,
  MappingMode,
  PairDashboardFixture,
  PairId,
} from "./features/dashboard/types";
import { PAIR_IDS } from "./features/dashboard/types";
import { coverageQualityStatus, worstQualityStatus } from "./features/dashboard/coverage-quality";
import type { ISODate } from "./domain";

const DEFAULT_PAIR_ID: PairId = "canola-matif-rapeseed";
const applicationAnalysisCaches = new WeakMap<
  BrowserProductionDashboardApplication,
  Map<string, Promise<DashboardCurrentKnowledgeResult>>
>();

const OverviewTab = lazy(() =>
  import("./features/overview/OverviewTab").then((module) => ({ default: module.OverviewTab })),
);
const SpreadMonitorTab = lazy(() =>
  import("./features/spread/SpreadMonitorTab").then((module) => ({ default: module.SpreadMonitorTab })),
);
const SeasonalityTab = lazy(() =>
  import("./features/seasonality/SeasonalityTab").then((module) => ({ default: module.SeasonalityTab })),
);
const SignalLabTab = lazy(() =>
  import("./features/signals/SignalLabTab").then((module) => ({ default: module.SignalLabTab })),
);
const RegimeResearchTab = lazy(() =>
  import("./features/regime/RegimeResearchTab").then((module) => ({ default: module.RegimeResearchTab })),
);

function analysisRequestKey(request: DashboardAnalysisRequest, retryKey: number): string {
  return JSON.stringify({ retryKey, request });
}

function analyzeProduction(
  application: BrowserProductionDashboardApplication,
  request: DashboardAnalysisRequest,
  retryKey: number,
): Promise<DashboardCurrentKnowledgeResult> {
  let cache = applicationAnalysisCaches.get(application);
  if (!cache) {
    cache = new Map();
    applicationAnalysisCaches.set(application, cache);
  }
  const key = analysisRequestKey(request, retryKey);
  const cached = cache.get(key);
  if (cached) return cached;
  const pending = application.analyze(request).catch((error: unknown) => {
    cache?.delete(key);
    throw error;
  });
  cache.set(key, pending);
  return pending;
}

function initialAnalysisControls(): DashboardAnalysisControls {
  return {
    ...DEFAULT_DASHBOARD_ANALYSIS_CONTROLS,
    quality: { ...DEFAULT_DASHBOARD_ANALYSIS_CONTROLS.quality },
  };
}

function errorDetail(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The production market-data service returned an unknown error.";
}

function withCoverageQuality(
  result: DashboardCurrentKnowledgeReady,
): DashboardCurrentKnowledgeReady {
  const sourceQuality = result.provenance.coverage.qualityStatus;
  const coverageQuality = coverageQualityStatus(sourceQuality);
  if (coverageQuality === "good") return result;
  const combinedQuality = worstQualityStatus(result.fixture.overview.quality, coverageQuality);
  return {
    ...result,
    fixture: {
      ...result.fixture,
      overview: {
        ...result.fixture.overview,
        quality: combinedQuality,
        qualityDetail: `Source coverage is ${sourceQuality}. ${result.fixture.overview.qualityDetail}`,
      },
    },
  };
}

type BootstrapState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly application: BrowserProductionDashboardApplication }
  | { readonly status: "error"; readonly detail: string };

type ActiveAnalysisState =
  | { readonly status: "loading"; readonly requestKey: string }
  | {
      readonly status: "ready";
      readonly requestKey: string;
      readonly result: DashboardCurrentKnowledgeReady;
    }
  | { readonly status: "not-available"; readonly requestKey: string; readonly detail: string }
  | { readonly status: "error"; readonly requestKey: string; readonly detail: string };

type OverviewAnalysisState =
  | { readonly status: "loading"; readonly requestKey: string }
  | {
      readonly status: "ready";
      readonly requestKey: string;
      readonly fixtures: readonly PairDashboardFixture[];
      readonly unavailableDetails: readonly string[];
    }
  | { readonly status: "error"; readonly requestKey: string; readonly detail: string };

type PairCatalog = Readonly<Record<PairId, DashboardPairCatalogEntry>>;
type ManualSelections = Readonly<Record<PairId, ContractPairSelection>>;

function pairCatalogRecord(entries: readonly DashboardPairCatalogEntry[]): PairCatalog {
  const records = Object.fromEntries(entries.map((entry) => [entry.id, entry]));
  for (const pairId of PAIR_IDS) {
    if (!records[pairId]) throw new Error(`Production catalog is missing pair ${pairId}.`);
  }
  return records as PairCatalog;
}

function initialManualSelection(entry: DashboardPairCatalogEntry): ContractPairSelection {
  if (entry.autoContracts) return entry.autoContracts;
  const legA = entry.manualLegAChoices[0];
  const legB = entry.manualLegBChoices[0];
  if (!legA || !legB) {
    throw new Error(`Production catalog has no selectable contracts for ${entry.id}.`);
  }
  return { legA, legB };
}

function createManualSelections(catalog: PairCatalog): ManualSelections {
  return Object.fromEntries(
    PAIR_IDS.map((pairId) => [pairId, initialManualSelection(catalog[pairId])]),
  ) as unknown as ManualSelections;
}

function reconcileManualSelections(
  current: ManualSelections,
  catalog: PairCatalog,
): ManualSelections {
  return Object.fromEntries(PAIR_IDS.map((pairId) => {
    const entry = catalog[pairId];
    const selected = current[pairId];
    const legA = entry.manualLegAChoices.find((choice) => choice.id === selected.legA.id);
    const legB = entry.manualLegBChoices.find((choice) => choice.id === selected.legB.id);
    return [pairId, legA && legB ? { legA, legB } : initialManualSelection(entry)];
  })) as unknown as ManualSelections;
}

export default function App() {
  const [bootstrapRetryKey, setBootstrapRetryKey] = useState(0);
  const [bootstrap, setBootstrap] = useState<BootstrapState>({ status: "loading" });

  useEffect(() => {
    let ignore = false;
    void loadBrowserProductionDashboardApplication()
      .then((application) => {
        if (!ignore) setBootstrap({ status: "ready", application });
      })
      .catch((error: unknown) => {
        if (!ignore) setBootstrap({ status: "error", detail: errorDetail(error) });
      });
    return () => {
      ignore = true;
    };
  }, [bootstrapRetryKey]);

  if (bootstrap.status === "ready") {
    return <Dashboard application={bootstrap.application} />;
  }

  return (
    <DashboardShell asOf={undefined}>
      <main className="flex flex-1 flex-col py-6">
        {bootstrap.status === "loading" ? (
          <DashboardPanelLoading label="production market data" />
        ) : (
          <AnalysisError
            detail={`${bootstrap.detail} No demo or synthetic fallback was used.`}
            onRetry={() => {
              setBootstrap({ status: "loading" });
              setBootstrapRetryKey((value) => value + 1);
            }}
          />
        )}
      </main>
    </DashboardShell>
  );
}

function Dashboard({ application }: { readonly application: BrowserProductionDashboardApplication }) {
  const initialCatalog = useMemo(() => pairCatalogRecord(application.pairs), [application]);
  const [activeTab, setActiveTab] = useState<DashboardTabId>("overview");
  const [selectedPairId, setSelectedPairId] = useState<PairId>(DEFAULT_PAIR_ID);
  const [mappingMode, setMappingMode] = useState<MappingMode>("auto");
  const [asOf, setAsOf] = useState(application.metadata.asOf);
  const [pairCatalog, setPairCatalog] = useState<PairCatalog>(initialCatalog);
  const [catalogAsOf, setCatalogAsOf] = useState(application.metadata.asOf);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [manualSelections, setManualSelections] = useState<ManualSelections>(() =>
    createManualSelections(initialCatalog),
  );
  const [analysisControls, setAnalysisControls] = useState<DashboardAnalysisControls>(
    initialAnalysisControls,
  );
  const [analysisRetryKey, setAnalysisRetryKey] = useState(0);
  const [activeAnalysis, setActiveAnalysis] = useState<ActiveAnalysisState>({
    status: "loading",
    requestKey: "initial",
  });
  const [overviewAnalysis, setOverviewAnalysis] = useState<OverviewAnalysisState>({
    status: "loading",
    requestKey: "initial",
  });

  useEffect(() => {
    if (catalogAsOf === asOf) return undefined;
    let ignore = false;
    void application.loadPairCatalog(asOf as ISODate)
      .then((entries) => {
        if (ignore) return;
        const next = pairCatalogRecord(entries);
        setPairCatalog(next);
        setManualSelections((current) => reconcileManualSelections(current, next));
        setCatalogAsOf(asOf);
      })
      .catch((error: unknown) => {
        if (!ignore) setCatalogError(errorDetail(error));
      });
    return () => {
      ignore = true;
    };
  }, [application, asOf, catalogAsOf]);

  const catalogEntry = pairCatalog[selectedPairId];
  const manualContracts = manualSelections[selectedPairId];
  const catalogIsCurrent = catalogAsOf === asOf;
  const catalogAutoContracts = catalogIsCurrent ? catalogEntry.autoContracts : null;
  const selectedContracts = mappingMode === "auto" ? catalogAutoContracts : manualContracts;
  const analyticalPanelActive = activeTab !== "overview";
  const overviewPanelActive = activeTab === "overview";
  const activeRequest = useMemo<DashboardAnalysisRequest>(() => ({
    pairPresetId: selectedPairId,
    asOf: asOf as ISODate,
    mapping: mappingMode === "auto"
      ? { mode: "auto" }
      : {
          mode: "manual",
          legAContractId: manualContracts.legA.dataContractId,
          legBContractId: manualContracts.legB.dataContractId,
        },
    controls: analysisControls,
  }), [
    analysisControls,
    asOf,
    manualContracts.legA.dataContractId,
    manualContracts.legB.dataContractId,
    mappingMode,
    selectedPairId,
  ]);
  const activeRequestKey = analysisRequestKey(activeRequest, analysisRetryKey);
  const overviewRequestKey = JSON.stringify({
    retryKey: analysisRetryKey,
    asOf,
    controls: analysisControls,
    scope: "overview-current-knowledge",
  });

  useEffect(() => {
    if (!analyticalPanelActive) return undefined;
    let ignore = false;
    const timer = window.setTimeout(() => {
      setActiveAnalysis({ status: "loading", requestKey: activeRequestKey });
      void analyzeProduction(application, activeRequest, analysisRetryKey)
        .then((result) => {
          if (ignore) return;
          setActiveAnalysis(
            result.status === "current-knowledge-ready"
              ? { status: "ready", requestKey: activeRequestKey, result: withCoverageQuality(result) }
              : { status: "not-available", requestKey: activeRequestKey, detail: result.detail },
          );
        })
        .catch((error: unknown) => {
          if (!ignore) {
            setActiveAnalysis({
              status: "error",
              requestKey: activeRequestKey,
              detail: errorDetail(error),
            });
          }
        });
    }, 25);
    return () => {
      ignore = true;
      window.clearTimeout(timer);
    };
  }, [activeRequest, activeRequestKey, analysisRetryKey, analyticalPanelActive, application]);

  useEffect(() => {
    if (!overviewPanelActive) return undefined;
    let ignore = false;
    const timer = window.setTimeout(() => {
      setOverviewAnalysis({ status: "loading", requestKey: overviewRequestKey });
      void Promise.all(application.pairs.map((entry) => analyzeProduction(application, {
        pairPresetId: entry.id,
        asOf: asOf as ISODate,
        mapping: { mode: "auto" },
        controls: analysisControls,
      }, analysisRetryKey)))
        .then((results) => {
          if (ignore) return;
          setOverviewAnalysis({
            status: "ready",
            requestKey: overviewRequestKey,
            fixtures: results.flatMap((result) =>
              result.status === "current-knowledge-ready" ? [withCoverageQuality(result).fixture] : [],
            ),
            unavailableDetails: results.flatMap((result) =>
              result.status === "not-available" ? [`${result.pairPresetId}: ${result.detail}`] : [],
            ),
          });
        })
        .catch((error: unknown) => {
          if (!ignore) {
            setOverviewAnalysis({
              status: "error",
              requestKey: overviewRequestKey,
              detail: errorDetail(error),
            });
          }
        });
    }, 25);
    return () => {
      ignore = true;
      window.clearTimeout(timer);
    };
  }, [analysisControls, analysisRetryKey, application, asOf, overviewPanelActive, overviewRequestKey]);

  const currentActiveAnalysis: ActiveAnalysisState = activeAnalysis.requestKey === activeRequestKey
    ? activeAnalysis
    : { status: "loading", requestKey: activeRequestKey };
  const currentOverviewAnalysis: OverviewAnalysisState = overviewAnalysis.requestKey === overviewRequestKey
    ? overviewAnalysis
    : { status: "loading", requestKey: overviewRequestKey };
  const computedOverviewContractsByPair: Readonly<Partial<Record<PairId, ContractPairSelection>>> =
    currentOverviewAnalysis.status === "ready"
      ? Object.fromEntries(currentOverviewAnalysis.fixtures.map((fixture) => [fixture.id, fixture.autoContracts]))
      : {};
  const computedSelectedOverview = currentOverviewAnalysis.status === "ready"
    ? currentOverviewAnalysis.fixtures.find((fixture) => fixture.id === selectedPairId)
    : undefined;
  const projectedActiveContracts = mappingMode === "manual"
    ? currentActiveAnalysis.status === "ready"
      ? currentActiveAnalysis.result.fixture.autoContracts
      : manualContracts
    : currentActiveAnalysis.status === "ready"
      ? currentActiveAnalysis.result.fixture.autoContracts
      : activeTab === "overview" && computedSelectedOverview
        ? computedSelectedOverview.autoContracts
        : selectedContracts;
  const computedFilterQuality = activeTab === "overview"
    ? computedSelectedOverview?.overview.quality
    : currentActiveAnalysis.status === "ready"
      ? currentActiveAnalysis.result.fixture.overview.quality
      : undefined;
  const filterQualityPending = activeTab === "overview"
    ? currentOverviewAnalysis.status === "loading"
    : currentActiveAnalysis.status === "loading";

  const updateManualContract = (leg: "legA" | "legB", contractId: string) => {
    const choices = leg === "legA" ? catalogEntry.manualLegAChoices : catalogEntry.manualLegBChoices;
    const contract = choices.find((choice) => choice.id === contractId);
    if (!contract) return;
    setManualSelections((current) => ({
      ...current,
      [selectedPairId]: {
        ...current[selectedPairId],
        [leg]: contract,
      },
    }));
  };

  const resetDashboard = () => {
    setActiveTab("overview");
    setSelectedPairId(DEFAULT_PAIR_ID);
    setMappingMode("auto");
    setAsOf(application.metadata.asOf);
    setPairCatalog(initialCatalog);
    setCatalogAsOf(application.metadata.asOf);
    setCatalogError(null);
    setManualSelections(createManualSelections(initialCatalog));
    setAnalysisControls(initialAnalysisControls());
  };

  return (
    <DashboardShell asOf={application.metadata.asOf}>
      <div className="mt-4">
        <DashboardTabs activeTab={activeTab} onChange={setActiveTab} />
      </div>

      <main className="flex flex-1 flex-col gap-6 py-6">
        <FilterBar
          fixture={catalogEntry}
          selectedPairId={selectedPairId}
          mappingMode={mappingMode}
          asOf={asOf}
          minAsOf={application.metadata.coverage.startDate ?? undefined}
          maxAsOf={application.metadata.asOf}
          activeContracts={projectedActiveContracts}
          manualContracts={manualContracts}
          qualityStatus={computedFilterQuality ?? "unavailable"}
          qualityLabel={computedFilterQuality
            ? undefined
            : filterQualityPending || !catalogIsCurrent
              ? "Quality pending"
              : "Quality N/A"}
          onPairChange={setSelectedPairId}
          onMappingModeChange={setMappingMode}
          onAsOfChange={(nextAsOf) => {
            if (nextAsOf) {
              setCatalogError(null);
              setAsOf(nextAsOf as ISODate);
            }
          }}
          onManualLegAChange={(contractId) => updateManualContract("legA", contractId)}
          onManualLegBChange={(contractId) => updateManualContract("legB", contractId)}
          onReset={resetDashboard}
        />

        {catalogError ? (
          <section role="alert" className="rounded-xl border border-warning/35 bg-warning/5 p-4 text-sm text-secondary">
            <p className="font-bold text-warning">Contract catalog unavailable for {asOf}</p>
            <p className="mt-1">{catalogError}</p>
          </section>
        ) : null}

        <section
          id={`panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${activeTab}`}
          tabIndex={0}
          className="min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-canvas"
        >
          <LazyPanelBoundary resetKey={activeTab} onReturnToOverview={() => setActiveTab("overview")}>
            <Suspense fallback={<DashboardPanelLoading label={`${activeTab} panel`} />}>
              {activeTab === "overview" && currentOverviewAnalysis.status === "loading" ? (
                <DashboardPanelLoading label="overview panel" />
              ) : null}
              {activeTab === "overview" && currentOverviewAnalysis.status === "error" ? (
                <AnalysisError detail={currentOverviewAnalysis.detail} onRetry={() => setAnalysisRetryKey((value) => value + 1)} />
              ) : null}
              {activeTab === "overview" && currentOverviewAnalysis.status === "ready" && currentOverviewAnalysis.fixtures.length === 0 ? (
                <AnalysisUnavailable
                  title="Overview analysis unavailable"
                  reason={currentOverviewAnalysis.unavailableDetails.join(" ") || `No validated pair snapshot is available through ${asOf}.`}
                />
              ) : null}
              {activeTab === "overview" && currentOverviewAnalysis.status === "ready" && currentOverviewAnalysis.fixtures.length > 0 ? (
                <>
                  <OverviewTab
                    fixtures={currentOverviewAnalysis.fixtures}
                    selectedPairId={selectedPairId}
                    computedContractsByPair={computedOverviewContractsByPair}
                    onPairSelect={setSelectedPairId}
                  />
                  {currentOverviewAnalysis.unavailableDetails.length > 0 ? (
                    <section role="status" tabIndex={0} className="mt-6 rounded-xl border border-warning/35 bg-warning/5 p-4 text-xs leading-relaxed text-secondary">
                      <p className="font-bold text-warning">Partial overview</p>
                      {currentOverviewAnalysis.unavailableDetails.map((detail) => <p key={detail} className="mt-1">{detail}</p>)}
                    </section>
                  ) : null}
                </>
              ) : null}
              {activeTab !== "overview" && currentActiveAnalysis.status === "loading" ? (
                <DashboardPanelLoading label={`${activeTab} panel`} />
              ) : null}
              {activeTab !== "overview" && currentActiveAnalysis.status === "error" ? (
                <AnalysisError detail={currentActiveAnalysis.detail} onRetry={() => setAnalysisRetryKey((value) => value + 1)} />
              ) : null}
              {activeTab !== "overview" && currentActiveAnalysis.status === "not-available" ? (
                <AnalysisUnavailable
                  title={mappingMode === "manual"
                    ? "Analytics unavailable for this manual contract pair"
                    : "Analytics unavailable for this contract episode"}
                  reason={currentActiveAnalysis.detail}
                  activePair={selectedContracts
                    ? `${selectedContracts.legA.label} versus ${selectedContracts.legB.label}`
                    : undefined}
                />
              ) : null}
              {activeTab === "spread" && currentActiveAnalysis.status === "ready" ? (
                <SpreadMonitorTab
                  fixture={currentActiveAnalysis.result.fixture}
                  diagnostics={currentActiveAnalysis.result.diagnostics}
                  activeContracts={currentActiveAnalysis.result.fixture.autoContracts}
                  asOf={asOf}
                  analysisWindow={analysisControls.window}
                  analysisMetric={analysisControls.metric}
                  onAnalysisWindowChange={(window) => setAnalysisControls((current) => ({ ...current, window }))}
                  onAnalysisMetricChange={(metric) => setAnalysisControls((current) => ({ ...current, metric }))}
                />
              ) : null}
              {activeTab === "seasonality" && currentActiveAnalysis.status === "ready" ? (
                <SeasonalityTab
                  fixture={currentActiveAnalysis.result.fixture}
                  activeContracts={currentActiveAnalysis.result.fixture.autoContracts}
                  asOf={asOf}
                />
              ) : null}
              {activeTab === "signals" && currentActiveAnalysis.status === "ready" ? (
                <SignalLabTab
                  fixture={currentActiveAnalysis.result.fixture}
                  diagnostics={currentActiveAnalysis.result.diagnostics}
                  activeContracts={currentActiveAnalysis.result.fixture.autoContracts}
                  asOf={asOf}
                  controls={analysisControls}
                  onControlsChange={setAnalysisControls}
                />
              ) : null}
              {activeTab === "regime" && currentActiveAnalysis.status === "ready" ? (
                <RegimeResearchTab
                  fixture={currentActiveAnalysis.result.fixture}
                  asOf={asOf}
                  windowSize={analysisControls.window}
                  onWindowSizeChange={(window) => setAnalysisControls((current) => ({ ...current, window }))}
                />
              ) : null}
            </Suspense>
          </LazyPanelBoundary>
        </section>
      </main>

      <footer className="border-t border-divider-subtle py-4 text-xs leading-relaxed text-faint">
        Source: {application.metadata.source.name} · Actual-contract latest-release research · Historical revision point-in-time reconstruction is unavailable · Not investment advice.
      </footer>
    </DashboardShell>
  );
}

function DashboardShell({
  asOf,
  children,
}: {
  readonly asOf: string | undefined;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-canvas text-primary">
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col p-4 sm:p-6">
        <DashboardHeader asOf={asOf} />
        {children}
      </div>
    </div>
  );
}

function DashboardPanelLoading({ label }: { readonly label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-xl border border-divider-subtle bg-panel p-6"
      data-testid="dashboard-panel-loading"
    >
      <div className="h-2 w-24 animate-pulse rounded-full bg-accent/45" />
      <div className="mt-4 h-6 w-72 max-w-full animate-pulse rounded bg-raised" />
      <div className="mt-3 h-3 w-full max-w-2xl animate-pulse rounded bg-raised/70" />
      <p className="mt-5 text-xs font-semibold text-muted">Loading {label}…</p>
    </div>
  );
}
